import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import { WithLoader } from 'Components/WithLoader'
import SchoolCalendarDetails from 'Services/SchoolManagement/Features/SchoolCalendar/Components/SchoolCalendarDetails'
import SchoolCalendarList from 'Services/SchoolManagement/Features/SchoolCalendar/Components/SchoolCalendarList'
import useSchoolCalendarsWithParams from 'Services/SchoolManagement/Features/SchoolCalendar/Hooks'
import {
  useDeleteSingleSchoolCalendarMutation,
  useGetSingleSchoolCalendarQuery
} from 'Services/SchoolManagement/Features/SchoolCalendar/schoolCalendarApi'
import { SchoolCalendarTabType } from 'Services/SchoolManagement/Features/SchoolCalendar/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC, SORT_ORDER_DESC } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import { useSelectedPta } from 'Util/hooks/useSelectedPta'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Divider, Tab, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

function SchoolCalendarListPage() {
  const navigate = useNavigate()
  const { confirm } = useConfirm()

  const {
    schoolCalendars,
    isFetching,
    params,
    setParams,
    totalResults,
    setSelectedSchoolCalendarId,
    selectedSchoolCalendarId
  } = useSchoolCalendarsWithParams()

  const { data: singleSchoolCalendar, isFetching: isSingleSchoolCalendarFetching } =
    useGetSingleSchoolCalendarQuery(selectedSchoolCalendarId ?? '', {
      skip: !selectedSchoolCalendarId
    })
  useSelectedPta(singleSchoolCalendar?.codespace_id)

  const [deleteSingleSchoolCalendar, { isLoading: isDeleteLoading }] =
    useDeleteSingleSchoolCalendarMutation()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedSchoolCalendarId)

  const breadcrumbs = [
    {
      title: t`Home`,
      link: '/school-management/'
    },
    {
      title: t`Calendars`
    }
  ]

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const onRowClick = (id: string) => {
    setSelectedSchoolCalendarId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setSelectedSchoolCalendarId('')
    setIsDrawerOpen(false)
  }

  const handleDelete = () => {
    if (!selectedSchoolCalendarId) return

    deleteSingleSchoolCalendar(selectedSchoolCalendarId)
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Calendar successfully deleted.`)
        handleDrawerClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the calendar.`)
      })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Calendars`}</Typography>
        <Box mt={4}>
          <ListActions>
            <CustomButton
              onClick={() => navigate('/school-management/school-calendars/create/')}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new calendar`}
            </CustomButton>
          </ListActions>
          <Box pt={1}>
            <SchoolCalendarList
              rows={schoolCalendars}
              isLoading={isFetching || isDeleteLoading}
              onRowClick={onRowClick}
              selectedSchoolCalendarId={selectedSchoolCalendarId}
              params={params}
              handleRequestSort={handleRequestSort}
              handleChangePage={handleChangePage}
              count={totalResults}
              page={
                params.start_index
                  ? Number.parseInt(params.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                  : 0
              }
            />
          </Box>
          <DrawerRight
            open={isDrawerOpen}
            onClose={handleDrawerClose}
            topBar={
              <Box>
                <DeleteButton
                  sx={{ mr: 1 }}
                  onClick={() =>
                    confirm({
                      title: t`Are you sure?`,
                      message: t`Are you sure you want to delete the calendar?`,
                      handleConfirm: () => {
                        handleDelete()
                      }
                    })
                  }
                >{t`Delete`}</DeleteButton>
                <CustomButton
                  onClick={() =>
                    navigate(`/school-management/school-calendars/${selectedSchoolCalendarId}/edit`)
                  }
                  size="small"
                  startIcon={<EditIcon />}
                >{t`Edit`}</CustomButton>
              </Box>
            }
          >
            <WithLoader loading={isSingleSchoolCalendarFetching || isDeleteLoading}>
              {singleSchoolCalendar && (
                <TabContext value={SchoolCalendarTabType.DETAILS}>
                  <TabList aria-label={t`Details`}>
                    <Tab label={t`Details`} value={SchoolCalendarTabType.DETAILS} />
                  </TabList>
                  <Divider />
                  <TabPanel sx={{ px: 0, pt: 0 }} value={SchoolCalendarTabType.DETAILS}>
                    <SchoolCalendarDetails schoolCalendar={singleSchoolCalendar} />
                  </TabPanel>
                </TabContext>
              )}
            </WithLoader>
          </DrawerRight>
        </Box>
      </Box>
    </Box>
  )
}

export default SchoolCalendarListPage
